import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Description as CsvIcon,
  PictureAsPdf as PdfIcon,
  Code as JsonIcon,
  Image as ImageIcon,
  TextSnippet as TextIcon,
  Article as DocIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { formatFileSize } from '../utils/sampleResponses';

/**
 * FilePreviewDialog - Preview files in a modal dialog
 * Supports: CSV (table), JSON (formatted), Text, Images, PDF (iframe)
 */
const FilePreviewDialog = ({
  open,
  onClose,
  file,
  fileContent,
  isLoading,
  error,
  colors,
  onDownload,
}) => {
  const [parsedContent, setParsedContent] = useState(null);

  // Parse content based on file type
  useEffect(() => {
    if (!fileContent || !file) {
      setParsedContent(null);
      return;
    }

    const mimeType = file.mimeType || '';

    try {
      if (mimeType === 'text/csv' || file.name?.endsWith('.csv')) {
        // Parse CSV into rows
        const lines = fileContent.split('\n').filter(line => line.trim());
        const rows = lines.map(line => {
          // Simple CSV parsing (handles basic cases)
          const cells = [];
          let current = '';
          let inQuotes = false;
          for (let char of line) {
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              cells.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          cells.push(current.trim());
          return cells;
        });
        setParsedContent({ type: 'csv', headers: rows[0], rows: rows.slice(1) });
      } else if (mimeType === 'application/json' || file.name?.endsWith('.json')) {
        // Parse and format JSON
        const json = JSON.parse(fileContent);
        setParsedContent({ type: 'json', data: JSON.stringify(json, null, 2) });
      } else if (mimeType.startsWith('image/')) {
        // Image - content should be base64 or URL
        setParsedContent({ type: 'image', data: fileContent });
      } else if (mimeType === 'application/pdf') {
        // PDF - content should be URL or base64
        setParsedContent({ type: 'pdf', data: fileContent });
      } else {
        // Default to text
        setParsedContent({ type: 'text', data: fileContent });
      }
    } catch (e) {
      console.error('Error parsing file content:', e);
      setParsedContent({ type: 'text', data: fileContent });
    }
  }, [fileContent, file]);

  // Get file icon based on type
  const getFileIcon = () => {
    const mimeType = file?.mimeType || '';
    if (mimeType === 'text/csv' || file?.name?.endsWith('.csv')) {
      return <CsvIcon sx={{ color: '#22c55e' }} />;
    } else if (mimeType === 'application/json' || file?.name?.endsWith('.json')) {
      return <JsonIcon sx={{ color: '#f59e0b' }} />;
    } else if (mimeType === 'application/pdf') {
      return <PdfIcon sx={{ color: '#ef4444' }} />;
    } else if (mimeType.startsWith('image/')) {
      return <ImageIcon sx={{ color: '#3b82f6' }} />;
    } else if (mimeType.startsWith('text/')) {
      return <TextIcon sx={{ color: '#71717a' }} />;
    } else if (mimeType.includes('document') || mimeType.includes('word')) {
      return <DocIcon sx={{ color: '#3b82f6' }} />;
    }
    return <FileIcon sx={{ color: '#71717a' }} />;
  };

  if (!file) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: colors.background,
          borderRadius: 0,
          height: '85vh',
          maxHeight: '900px',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: colors.surface,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {getFileIcon()}
          <Box>
            <Typography variant="subtitle1" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
              {file.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                {formatFileSize(file.size)}
              </Typography>
              <Chip
                label="Preview"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  bgcolor: 'rgba(34, 197, 94, 0.15)',
                  color: '#22c55e',
                  borderRadius: 0,
                }}
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onDownload && (
            <IconButton
              onClick={() => onDownload(file)}
              sx={{
                color: colors.textSecondary,
                border: `1px solid ${colors.border}`,
                borderRadius: 0,
                '&:hover': {
                  color: '#22c55e',
                  borderColor: '#22c55e',
                },
              }}
              title="Download file"
            >
              <DownloadIcon />
            </IconButton>
          )}
          <IconButton
            onClick={onClose}
            sx={{ color: colors.textSecondary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Loading state */}
        {isLoading && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 2,
          }}>
            <CircularProgress sx={{ color: '#22c55e' }} />
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              Loading preview...
            </Typography>
          </Box>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 2,
            color: colors.textMuted,
          }}>
            <FileIcon sx={{ fontSize: 64, opacity: 0.3 }} />
            <Typography variant="body1" sx={{ color: '#ef4444' }}>
              Unable to preview this file
            </Typography>
            <Typography variant="body2">
              {error}
            </Typography>
          </Box>
        )}

        {/* CSV Table View */}
        {!isLoading && !error && parsedContent?.type === 'csv' && (
          <TableContainer
            component={Paper}
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              bgcolor: colors.background,
              borderRadius: 0,
              boxShadow: 'none',
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {parsedContent.headers?.map((header, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        bgcolor: colors.surface,
                        color: colors.textPrimary,
                        fontWeight: 600,
                        borderBottom: `2px solid ${colors.border}`,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {parsedContent.rows?.slice(0, 100).map((row, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    sx={{
                      '&:nth-of-type(odd)': { bgcolor: colors.surface },
                      '&:hover': { bgcolor: colors.hoverBg },
                    }}
                  >
                    {row.map((cell, cellIndex) => (
                      <TableCell
                        key={cellIndex}
                        sx={{
                          color: colors.textPrimary,
                          borderBottom: `1px solid ${colors.border}`,
                        }}
                      >
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {parsedContent.rows?.length > 100 && (
              <Box sx={{ p: 2, textAlign: 'center', bgcolor: colors.surface }}>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  Showing first 100 of {parsedContent.rows.length} rows. Download for full data.
                </Typography>
              </Box>
            )}
          </TableContainer>
        )}

        {/* JSON View */}
        {!isLoading && !error && parsedContent?.type === 'json' && (
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              p: 2,
              bgcolor: colors.surface,
            }}
          >
            <pre
              style={{
                margin: 0,
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                color: colors.textPrimary,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {parsedContent.data}
            </pre>
          </Box>
        )}

        {/* Text View */}
        {!isLoading && !error && parsedContent?.type === 'text' && (
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              p: 2,
              bgcolor: colors.surface,
            }}
          >
            <pre
              style={{
                margin: 0,
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                color: colors.textPrimary,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {parsedContent.data}
            </pre>
          </Box>
        )}

        {/* Image View */}
        {!isLoading && !error && parsedContent?.type === 'image' && (
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
              bgcolor: colors.surface,
            }}
          >
            <img
              src={parsedContent.data}
              alt={file.name}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
        )}

        {/* PDF View */}
        {!isLoading && !error && parsedContent?.type === 'pdf' && (
          <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
            <iframe
              src={parsedContent.data}
              title={file.name}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </Box>
        )}

        {/* Unsupported type */}
        {!isLoading && !error && !parsedContent && fileContent && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 2,
            color: colors.textMuted,
          }}>
            <FileIcon sx={{ fontSize: 64, opacity: 0.3 }} />
            <Typography variant="body1">
              Preview not available for this file type
            </Typography>
            <Typography variant="body2">
              Download the file to view its contents
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FilePreviewDialog;
